import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { KeycloakTokenParsed, KeycloakLoginOptions } from 'keycloak-js'
import keycloak from '@/lib/keycloak'
import api from '@/lib/api'
import { getApolloClient } from '@/lib/apollo-client'
import { ensureSocialLoginProvisioned } from '@/lib/social-provision'
import { refreshKeycloakToken } from '@/lib/auth-token'
import { useIdleLogout } from '@/hooks/useIdleLogout'
import { IdleWarningDialog } from '../dialog/IdleWarningDialog'

const DEFAULT_IDLE_TIMEOUT_MS = 20 * 60 * 1000
const IDLE_WARNING_MS = 60 * 1000

interface AuthContextType {
  isAuthenticated: boolean
  token: string | null
  user: KeycloakTokenParsed | null
  login: () => void
  logout: () => void
  loading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthSessionProvider')
  }
  return context
}

// Shim for next-auth useSession
export const useSession = () => {
  const { isAuthenticated, token, user, loading } = useAuth()
  const tokenWithClaims = user as (KeycloakTokenParsed & Record<string, unknown>) | null
  return {
    data: isAuthenticated
      ? {
          accessToken: token || undefined,
          user: tokenWithClaims
            ? {
                name: (tokenWithClaims.name as string | undefined) ?? null,
                email: (tokenWithClaims.email as string | undefined) ?? null,
                image: null as string | null,
              }
            : undefined,
          expires: '', // Dummy value
          accessTokenExpired: 0, // Dummy value
        }
      : null,
    status: loading ? 'loading' : isAuthenticated ? 'authenticated' : 'unauthenticated',
  }
}

export default function AuthSessionProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [token, setToken] = useState<string | null>(null)
  const [user, setUser] = useState<KeycloakTokenParsed | null>(null)
  const [loading, setLoading] = useState(!!keycloak.authenticated)
  const provisioningRef = useRef(false)
  const [inactivityTimeoutMs, setInactivityTimeoutMs] = useState(DEFAULT_IDLE_TIMEOUT_MS)
  const [idleWarningOpen, setIdleWarningOpen] = useState(false)
  const [idleWarningSeconds, setIdleWarningSeconds] = useState(Math.round(IDLE_WARNING_MS / 1000))

  const handleAuthenticated = async () => {
    if (keycloak.token) {
      api.axios.defaults.headers.common.Authorization = `Bearer ${keycloak.token}`

      // Ensure the patient account exists in our DB before marking as authenticated.
      // This blocks downstream API calls until provisioning completes (first social login only).
      if (!provisioningRef.current) {
        provisioningRef.current = true
        try {
          await ensureSocialLoginProvisioned(keycloak.token)
        } finally {
          provisioningRef.current = false
        }
      }
    }

    setToken(keycloak.token || null)
    setUser(keycloak.tokenParsed || null)
    setIsAuthenticated(true)
    setLoading(false)
  }

  useEffect(() => {
    if (keycloak.authenticated) {
      handleAuthenticated()
    }

    keycloak.onTokenExpired = () => {
      refreshKeycloakToken()
        .then((token) => {
          setToken(token || null)
          if (token) {
            api.axios.defaults.headers.common.Authorization = `Bearer ${token}`
          }
        })
        .catch((err) => {
          console.error('Failed to refresh token', err)
          // Clear Apollo cache and logout with redirect
          const apolloClient = getApolloClient()
          apolloClient.clearStore()
          delete api.axios.defaults.headers.common.Authorization
          keycloak.logout({ redirectUri: window.location.origin })
        })
    }

    keycloak.onAuthSuccess = () => {
      handleAuthenticated()
    }

    keycloak.onAuthLogout = () => {
      setIsAuthenticated(false)
      setToken(null)
      setUser(null)
    }
  }, [])

  const login = () => keycloak.login()
  const logout = () => {
    // Clear Apollo cache on logout
    const apolloClient = getApolloClient()
    apolloClient.clearStore()
    // Clear API authorization header
    delete api.axios.defaults.headers.common.Authorization
    // Logout from Keycloak with redirect back to home
    keycloak.logout({ redirectUri: window.location.origin })
  }

  useEffect(() => {
    if (!isAuthenticated) return
    let cancelled = false
    api.settings
      .getInactivityTimeoutMinutes()
      .then((res) => {
        if (!cancelled && res?.inactivityTimeoutMinutes) setInactivityTimeoutMs(res.inactivityTimeoutMinutes * 60 * 1000)
      })
      .catch(() => {
        console.error('Failed to get inactivity timeout minutes')
      })
    return () => {
      cancelled = true
    }
  }, [isAuthenticated])

  const { reset: resetIdleTimer } = useIdleLogout({
    timeoutMs: inactivityTimeoutMs,
    enabled: isAuthenticated,
    onLogout: logout,
    warningMs: IDLE_WARNING_MS,
    onWarn: (remainingMs) => {
      setIdleWarningSeconds(Math.max(1, Math.round(remainingMs / 1000)))
      setIdleWarningOpen(true)
    },
    onActive: () => setIdleWarningOpen(false),
  })

  return (
    <AuthContext.Provider value={{ isAuthenticated, token, user, login, logout, loading }}>
      {children}
      <IdleWarningDialog
        open={idleWarningOpen}
        initialSeconds={idleWarningSeconds}
        onStay={() => {
          setIdleWarningOpen(false)
          resetIdleTimer()
        }}
      />
    </AuthContext.Provider>
  )
}

export const signIn = (provider?: string, options?: { callbackUrl?: string } & KeycloakLoginOptions) => {
  const { callbackUrl, ...keycloakOptions } = options ?? {}
  // Keycloak requires an absolute URL for redirectUri
  const resolvedRedirectUri = callbackUrl
    ? callbackUrl.startsWith('http')
      ? callbackUrl
      : `${window.location.origin}${callbackUrl.startsWith('/') ? '' : '/'}${callbackUrl}`
    : keycloakOptions.redirectUri
  keycloak.login({
    ...keycloakOptions,
    redirectUri: resolvedRedirectUri,
  })
}

export const signOut = (options?: { redirectUri?: string }) => {
  // Clear Apollo cache on logout
  const apolloClient = getApolloClient()
  apolloClient.clearStore()
  // Clear API authorization header
  delete api.axios.defaults.headers.common.Authorization
  // Logout from Keycloak with redirect back to home (or provided uri)
  keycloak.logout({ redirectUri: options?.redirectUri ?? window.location.origin })
}
